/**
 * Overrides de tipografia mobile (Fase 2) — palco, modal e export.
 * @package gepetto-frontend
 */

import { BREAKPOINT_MOBILE_MAX, EDIT_BREAKPOINT_MOBILE } from '../constants/responsiveBreakpoints';
import { patchResponsiveMobile } from './responsiveLayout';
import {
  SECTION_HEADER_MOBILE_STYLE_KEYS,
  resolveSectionHeaderLineHeight,
  sectionHeaderSubtitleMarginTopCss,
  sectionHeaderWeightNumber,
} from './sectionHeaderTypography';

export const MOBILE_LAYOUT_KEYS = ['marginObj', 'paddingObj', 'orderSmall', 'centerOnSmall', 'paddingTop', 'paddingBottom', 'hideOnMobile', 'centerOnMobile'];

export const MOBILE_TYPOGRAPHY_KEYS = [
  'wrapperFont',
  'titleFontSize',
  'subtitleFontSize',
  'sectionHeaderTitleFontPx',
  'sectionHeaderSubtitleFontPx',
  'titleFontPx',
  'subtitleFontPx',
  'cardTitleFontPx',
  'cardTextFontPx',
  'cardPriceFontPx',
  'fontSize',
  'h1Size',
  'h2Size',
  'h3Size',
  'pSize',
];

const TYPO_FIELDS_BY_TYPE = {
  global: [
    { key: 'h1Size', labelKey: 'mobileGlobalH1', min: 16, max: 72, defaultDesktop: 36 },
    { key: 'h2Size', labelKey: 'mobileGlobalH2', min: 14, max: 60, defaultDesktop: 30 },
    { key: 'h3Size', labelKey: 'mobileGlobalH3', min: 12, max: 48, defaultDesktop: 24 },
    { key: 'pSize', labelKey: 'mobileGlobalP', min: 10, max: 36, defaultDesktop: 16 },
  ],
  'title-basic': [
    { key: 'titleFontSize', labelKey: 'titleBasicTitleSize', min: 10, max: 72 },
    { key: 'subtitleFontSize', labelKey: 'titleBasicSubtitleSize', min: 10, max: 36 },
  ],
  'section-header': [
    { key: 'sectionHeaderTitleFontPx', labelKey: 'sectionHeaderTitleFontSize', min: 10, max: 72 },
    { key: 'sectionHeaderSubtitleFontPx', labelKey: 'sectionHeaderSubtitleFontSize', min: 10, max: 36 },
  ],
  'responsive-table': [
    { key: 'fontSize', labelKey: 'tableFontSize', min: 11, max: 22 },
  ],
  'social-card': [
    { key: 'titleFontPx', labelKey: 'socialCardTitleFontSize', min: 12, max: 34 },
    { key: 'subtitleFontPx', labelKey: 'socialCardSubtitleFontSize', min: 10, max: 26 },
  ],
  'card-slider': [
    { key: 'cardTitleFontPx', labelKey: 'cardSliderTitleFont', min: 10, max: 72 },
    { key: 'cardTextFontPx', labelKey: 'cardSliderTextFont', min: 10, max: 72 },
  ],
  'card-shop': [
    { key: 'cardTitleFontPx', labelKey: 'cardSliderTitleFont', min: 10, max: 72 },
    { key: 'cardTextFontPx', labelKey: 'cardSliderTextFont', min: 10, max: 72 },
    { key: 'cardPriceFontPx', labelKey: 'cardShopPriceFont', min: 10, max: 72 },
  ],
  'video-card-slider': [
    { key: 'cardTitleFontPx', labelKey: 'cardSliderTitleFont', min: 10, max: 72 },
  ],
};

const WRAPPER_FONT_EXCLUDED = new Set([
  'row', 'column', 'global', 'title-basic', 'section-header', 'table-of-contents', 'responsive-table', 'social-card',
  'flowchart-widget', 'timeline', 'header-menu', 'site-menu', 'site-footer', 'site-login', 'site-maintenance', 'count-up', 'countdown', 'page-break', 'geppetto-backpack', 'htmlTemplate',
  'image', 'video', 'panda-video', 'h5p', 'chart-widget', 'checkpoint', 'reinforcement-quiz',
]);

const DEFAULT_WRAPPER_FIELDS = [
  { key: 'wrapperFont', labelKey: 'fontSizeLabel', min: 10, max: 60 },
];

function parsePx(val) {
  const n = parseInt(val, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function getTypographyFieldsForWidget(widgetType) {
  if (widgetType === 'global') return TYPO_FIELDS_BY_TYPE.global;
  if (TYPO_FIELDS_BY_TYPE[widgetType]) return TYPO_FIELDS_BY_TYPE[widgetType];
  if (WRAPPER_FONT_EXCLUDED.has(widgetType)) return null;
  return DEFAULT_WRAPPER_FIELDS;
}

export function supportsMobileTypography(widgetType) {
  return getTypographyFieldsForWidget(widgetType) != null;
}

export function getMobileTypographyOverrides(content) {
  return content?.responsive?.mobile || {};
}

export function hasMobileTypographyOverrides(content, widgetType) {
  const fields = getTypographyFieldsForWidget(widgetType);
  if (!fields) return false;
  const mobile = getMobileTypographyOverrides(content);
  if (fields.some((f) => parsePx(mobile[f.key]) != null)) return true;
  if (widgetType !== 'section-header') return false;
  return SECTION_HEADER_MOBILE_STYLE_KEYS.some((k) => mobile[k] !== undefined && mobile[k] !== null && mobile[k] !== '');
}

export function clearMobileTypographyOverrides(content) {
  const mobile = { ...(content?.responsive?.mobile || {}) };
  MOBILE_TYPOGRAPHY_KEYS.forEach((k) => { delete mobile[k]; });
  SECTION_HEADER_MOBILE_STYLE_KEYS.forEach((k) => { delete mobile[k]; });
  const responsive = { ...(content?.responsive || {}) };
  if (Object.keys(mobile).length === 0) {
    delete responsive.mobile;
  } else {
    responsive.mobile = mobile;
  }
  return { ...content, responsive };
}

/** Mescla overrides mobile de tipografia no conteúdo efetivo do palco. */
export function resolveWidgetContentForEdit(content, widgetType, editBreakpoint) {
  const base = content && typeof content === 'object' ? content : {};
  if (editBreakpoint !== EDIT_BREAKPOINT_MOBILE) return base;
  const fields = getTypographyFieldsForWidget(widgetType);
  if (!fields) return base;
  const mobile = getMobileTypographyOverrides(base);
  const out = { ...base };
  fields.forEach((f) => {
    const px = parsePx(mobile[f.key]);
    if (px != null) out[f.key] = px;
  });
  if (widgetType === 'section-header') {
    SECTION_HEADER_MOBILE_STYLE_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(mobile, k) && mobile[k] !== '' && mobile[k] != null) {
        out[k] = mobile[k];
      }
    });
  }
  return out;
}

export function setMobileTypographyField(content, key, value) {
  const responsive = { ...(content?.responsive || {}) };
  const mobile = { ...(responsive.mobile || {}) };
  if (value === undefined || value === null || value === '') delete mobile[key];
  else mobile[key] = value;
  if (Object.keys(mobile).length === 0) delete responsive.mobile;
  else responsive.mobile = mobile;
  return { ...content, responsive: Object.keys(responsive).length ? responsive : undefined };
}

export function resolveGlobalConfigForEdit(globalConfig, editBreakpoint) {
  const base = globalConfig && typeof globalConfig === 'object' ? globalConfig : {};
  if (editBreakpoint !== EDIT_BREAKPOINT_MOBILE) return base;
  const mobile = base.responsive?.mobile;
  if (!mobile) return base;
  const out = { ...base };
  ['h1Size', 'h2Size', 'h3Size', 'pSize'].forEach((k) => {
    const px = parsePx(mobile[k]);
    if (px != null) out[k] = px;
  });
  return out;
}

/** Regras @media de tipografia por widget no export. */
export function pushWidgetMobileTypographyRules(rules, widgetId, widgetType, content) {
  const mobile = getMobileTypographyOverrides(content);
  const mq = `@media (max-width: ${BREAKPOINT_MOBILE_MAX}px)`;
  const wSel = `.madri-moodle-layout .madri-widget-${widgetId}`;
  const wf = parsePx(mobile.wrapperFont);

  if (wf != null && !['title-basic', 'section-header', 'responsive-table', 'social-card', 'card-slider', 'card-shop', 'video-card-slider'].includes(widgetType)) {
    rules.push(`${mq} { ${wSel} { font-size: ${wf}px !important; } }`);
  }

  if (widgetType === 'title-basic') {
    const t = parsePx(mobile.titleFontSize);
    const s = parsePx(mobile.subtitleFontSize);
    if (t != null) rules.push(`${mq} { ${wSel} .madri-gc-title-basic-h { font-size: ${t}px !important; } }`);
    if (s != null) rules.push(`${mq} { ${wSel} .madri-gc-title-basic-sub { font-size: ${s}px !important; } }`);
  }

  if (widgetType === 'section-header') {
    const t = parsePx(mobile.sectionHeaderTitleFontPx);
    const s = parsePx(mobile.sectionHeaderSubtitleFontPx);
    if (t != null) rules.push(`${mq} { ${wSel} .madri-gc-section-header__heading { font-size: ${t}px !important; } }`);
    if (s != null) rules.push(`${mq} { ${wSel} .madri-gc-section-header__sub { font-size: ${s}px !important; } }`);
    const pushWeight = (selector, raw) => {
      if (raw !== 'light' && raw !== 'normal' && raw !== 'bold') return;
      rules.push(`${mq} { ${wSel} ${selector} { font-weight: ${sectionHeaderWeightNumber(raw, 400)} !important; } }`);
    };
    const pushItalic = (selector, raw) => {
      if (raw !== true && raw !== false) return;
      rules.push(`${mq} { ${wSel} ${selector} { font-style: ${raw ? 'italic' : 'normal'} !important; } }`);
    };
    pushWeight('.madri-header .tag', mobile.sectionHeaderTagWeight);
    pushItalic('.madri-header .tag', mobile.sectionHeaderTagItalic);
    pushWeight('.madri-gc-section-header__heading', mobile.sectionHeaderTitleWeight);
    pushItalic('.madri-gc-section-header__heading', mobile.sectionHeaderTitleItalic);
    pushWeight('.madri-gc-section-header__sub', mobile.sectionHeaderSubtitleWeight);
    pushItalic('.madri-gc-section-header__sub', mobile.sectionHeaderSubtitleItalic);
    if (mobile.sectionHeaderTitleSubtitleGap != null && mobile.sectionHeaderTitleSubtitleGap !== '') {
      const gap = parseInt(mobile.sectionHeaderTitleSubtitleGap, 10);
      if (Number.isFinite(gap) && gap >= 0) {
        const marginTop = sectionHeaderSubtitleMarginTopCss(gap, resolveSectionHeaderLineHeight(content));
        rules.push(`${mq} { ${wSel} .madri-gc-section-header__sub { margin-top: ${marginTop} !important; } }`);
      }
    }
  }

  if (widgetType === 'card-slider' || widgetType === 'card-shop') {
    const t = parsePx(mobile.cardTitleFontPx);
    const s = parsePx(mobile.cardTextFontPx);
    if (t != null) rules.push(`${mq} { ${wSel} .madri-card-slider__title { font-size: ${t}px !important; } }`);
    if (s != null) rules.push(`${mq} { ${wSel} .madri-card-slider__text { font-size: ${s}px !important; } }`);
  }

  if (widgetType === 'card-shop') {
    const p = parsePx(mobile.cardPriceFontPx);
    if (p != null) rules.push(`${mq} { ${wSel} .madri-card-shop__price { font-size: ${p}px !important; } }`);
  }

  if (widgetType === 'video-card-slider') {
    const t = parsePx(mobile.cardTitleFontPx);
    if (t != null) rules.push(`${mq} { ${wSel} .madri-video-slider__title { font-size: ${t}px !important; } }`);
  }

  if (widgetType === 'social-card') {
    const t = parsePx(mobile.titleFontPx);
    const s = parsePx(mobile.subtitleFontPx);
    if (t != null) rules.push(`${mq} { ${wSel} .madri-social-card-title { font-size: ${t}px !important; } }`);
    if (s != null) rules.push(`${mq} { ${wSel} .madri-social-card-subtitle { font-size: ${s}px !important; } }`);
  }

  if (widgetType === 'responsive-table') {
    const fs = parsePx(mobile.fontSize);
    if (fs != null) rules.push(`${mq} { ${wSel} .madri-gc-table { font-size: ${fs}px !important; } }`);
  }
}

/** Tipografia global do curso (h1–p) no export mobile. */
export function pushGlobalMobileTypographyRules(rules, globalConfig, courseWrapperSelector = '.madri-moodle-layout') {
  const mobile = globalConfig?.responsive?.mobile;
  if (!mobile) return;
  const mq = `@media (max-width: ${BREAKPOINT_MOBILE_MAX}px)`;
  const h1 = parsePx(mobile.h1Size);
  const h2 = parsePx(mobile.h2Size);
  const h3 = parsePx(mobile.h3Size);
  const p = parsePx(mobile.pSize);
  if (h1 != null) {
    rules.push(`${mq} { ${courseWrapperSelector} h1:not(.madri-gc-section-header__heading) { font-size: ${h1}px !important; } }`);
  }
  if (h2 != null) {
    rules.push(`${mq} { ${courseWrapperSelector} h2:not(.madri-gc-section-header__heading) { font-size: ${h2}px !important; } }`);
  }
  if (h3 != null) {
    rules.push(`${mq} { ${courseWrapperSelector} h3:not(.madri-gc-section-header__heading) { font-size: ${h3}px !important; } }`);
  }
  if (p != null) {
    rules.push(`${mq} { ${courseWrapperSelector} p:not(.madri-gc-timeline-desc):not(.madri-gc-section-header__sub) { font-size: ${p}px !important; } }`);
  }
}

export function patchResponsiveMobileTypography(content, key, val) {
  const px = parseInt(val, 10);
  if (!Number.isFinite(px) || px <= 0) {
    const mobile = { ...(content?.responsive?.mobile || {}) };
    delete mobile[key];
    const responsive = { ...(content?.responsive || {}) };
    if (Object.keys(mobile).length === 0) delete responsive.mobile;
    else responsive.mobile = mobile;
    return { ...content, responsive };
  }
  return patchResponsiveMobile(content, { [key]: px });
}
